import "antd/dist/reset.css";
import { useEffect, useState } from "react";
import {
  Carousel,
  Button,
  Row,
  Col,
  Empty,
  Tag,
  Popconfirm,
  notification,
  Card as AntCard,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  IdcardOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import AddCardModal from "./AddCardModal";
import { controller } from "../../../../payment/stripe/controller";

/**
 * props:
 * - title?: string
 * - memberId: string (Required)
 * - onCardSelected?: (cardInfo | null) => void // CHANGE:
 */
export default function MemberCardsCarousel({
  title = "Payment Cards",
  memberId,
  onCardSelected,
}) {
  const [openModal, setOpenModal] = useState(false);
  const [cardList, setCardList] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const hasFetchedCards =
    Array.isArray(cardList?.payment_methods) &&
    cardList.payment_methods.length > 0;

  const getCards = async () => {
    if (!memberId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await controller.getCardList(memberId);
      if (response.status < 250) {
        setCardList(response.json);
      } else {
        notification.error({
          message: "Error Fetching Cards",
          description: "Could not retrieve the card list. Please try again.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Network Error",
        description: "A network error occurred while fetching cards.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCards();
    setSelectedCardId(null);
    if (onCardSelected) onCardSelected(null);
  }, [memberId]);

  useEffect(() => {
    if (!selectedCardId) return;
    const exists = (cardList?.payment_methods || []).some(
      (pm) => pm.id === selectedCardId
    );
    if (!exists) {
      setSelectedCardId(null);
      if (onCardSelected) onCardSelected(null);
    }
  }, [cardList, selectedCardId, onCardSelected]);

  const deleteCard = async (cardInfo) => {
    const pmId = cardInfo?.id;
    if (!memberId) return;
    try {
      const response = await controller.deleteCard(memberId, pmId);
      if (response.status < 250) {
        notification.success({
          message: "Card Deleted",
          description: "The card was successfully removed.",
          placement: "bottom",
        });
        getCards();
      } else {
        notification.error({
          message: "Failed to Delete Card",
          description: response.data?.detail || "An unexpected error occurred.",
          placement: "bottom",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Deleting Card",
        description: "Something went wrong while deleting the card.",
        placement: "bottom",
      });
      console.error(error);
    }
  };

  const handleCardSelect = (cardInfo) => {
    let newSelectedId = null;
    let selectedCardData = null;

    if (selectedCardId === cardInfo.id) {
      newSelectedId = null; // Deselect
      selectedCardData = null;
    } else {
      newSelectedId = cardInfo.id; // Select
      selectedCardData = cardInfo;
    }

    setSelectedCardId(newSelectedId);

    if (onCardSelected) {
      onCardSelected(selectedCardData);
    }
  };

  const makeDefaultCard = async (cardInfo) => {
    const pmId = cardInfo?.id;
    if (!memberId) return;
    try {
      const response = await controller.makeDefaultCard(memberId, pmId);
      if (response.status < 250) {
        notification.success({
          message: "Default Card Updated",
          description: "This card is now your default payment method.",
          placement: "bottom",
        });
        getCards();
      } else {
        notification.error({
          message: "Failed to Set Default Card",
          description: response.data?.detail || "An unexpected error occurred.",
          placement: "bottom",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Setting Default Card",
        description: "Something went wrong while setting the default card.",
        placement: "bottom",
      });
      console.error(error);
    }
  };

  // رندر هر کارت
  const renderCard = (c) => {
    const brand = (c.brand || "Card").toUpperCase();
    const cardNumber = `**** **** **** ${c.last4 || "••••"}`;
    const exp =
      c.exp_month && c.exp_year
        ? `${String(c.exp_month).padStart(2, "0")} / ${String(c.exp_year).slice(
            -2
          )}`
        : "--/--";
    const isSelected = selectedCardId === c.id;

    return (
      <AntCard
        onClick={() => handleCardSelect(c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardSelect(c);
          }
        }}
        style={{
          background:
            "linear-gradient(135deg, #f0f0f0 0%, #ffffff 70%, #f0f0f0 50%)",
          color: "#333",
          borderRadius: 16,
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
          border: isSelected ? "2px solid #0981C8" : "1px solid #939494ff",
          cursor: "pointer",
          overflow: "hidden",
          transition: "border 0.2s ease-in-out",
        }}
        bordered={false}
        bodyStyle={{ padding: "24px" }}
      >
        <Row
          justify="space-between"
          align="top"
          style={{ marginBottom: "0.5rem" }}
        >
          <Col>
            <IdcardOutlined style={{ fontSize: 36, color: "#555" }} />
          </Col>
          <Col style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isSelected && <Tag color="success">Selected</Tag>}
            <div style={{ fontSize: 24, fontWeight: "bold" }}>{brand}</div>
            {c.is_default && <Tag color="blue">Default</Tag>}
          </Col>
        </Row>
        <Row style={{ marginBottom: "1rem" }}>
          <Col span={24}>
            <div
              style={{
                fontSize: 22,
                fontFamily: "monospace",
                letterSpacing: 3,
                textAlign: "center",
              }}
            >
              {cardNumber}
            </div>
          </Col>
        </Row>
        <Row justify="space-between" align="bottom">
          <Col></Col>
          <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span>{exp}</span>
            <WifiOutlined
              style={{ fontSize: 24, transform: "rotate(90deg)" }}
            />
          </Col>
        </Row>
        <Row justify="end" gutter={8} style={{ marginTop: 24 }}>
          <Col>
            <Popconfirm
              title="Remove this card?"
              okText="Remove"
              cancelText="Cancel"
              onConfirm={() => deleteCard(c)}
            >
              <Button danger icon={<DeleteOutlined />} ghost>
                Remove
              </Button>
            </Popconfirm>
          </Col>
          <Col>
            <Button
              type="primary"
              ghost
              icon={<CheckOutlined />}
              disabled={c.is_default}
              onClick={(e) => {
                e.stopPropagation();
                makeDefaultCard(c);
              }}
            >
              Make default
            </Button>
          </Col>
        </Row>
      </AntCard>
    );
  };

  // const createSlides = () => {
  //   const slides = [];
  //   const paymentMethods = cardList?.payment_methods || [];
  //   for (let i = 0; i < paymentMethods.length; i += 2) {
  //     const card1 = paymentMethods[i];
  //     const card2 = paymentMethods[i + 1];
  //     slides.push(
  //       <div key={card1.id || i}>
  //         <Row gutter={[32, 16]} justify="center">
  //           <Col
  //             xs={24}
  //             md={card2 ? 12 : 24}
  //             style={{ maxWidth: 410, margin: !card2 ? "0 auto" : undefined }}
  //           >
  //             {renderCard(card1)}
  //           </Col>
  //           {card2 && (
  //             <Col xs={24} md={12} style={{ maxWidth: 410 }}>
  //               {renderCard(card2)}
  //             </Col>
  //           )}
  //         </Row>
  //       </div>
  //     );
  //   }
  //   return slides;
  // };

  const createSlides = () => {
    const paymentMethods = cardList?.payment_methods || [];

    return paymentMethods.map((card) => (
      <div key={card.id}>
        <Row justify="center">
          <Col xs={24} style={{ maxWidth: 410, margin: "0 auto" }}>
            {renderCard(card)}
          </Col>
        </Row>
      </div>
    ));
  };

  return (
    <>
      {openModal && (
        <AddCardModal
          open={openModal}
          close={() => {
            setOpenModal(false);
            getCards();
          }}
          memberId={memberId}
        />
      )}
      <div
        className="mp-carousel"
        style={{
          width: "100%",
          position: "relative",
          overflow: "visible",
          paddingBottom: hasFetchedCards ? 24 : 0,
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 12 }}
        >
          <Col>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#0981C8",
                marginBottom: "8px",
                marginLeft: "20px",
              }}
            >
              {title}
            </div>
          </Col>
          {/* <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenModal(true)}
            >
              Add Card
            </Button>
          </Col> */}
        </Row>

        {isLoading ? (
          <Empty description="Loading cards..." />
        ) : hasFetchedCards ? (
          <Carousel arrows draggable infinite={false}>
            {createSlides()}
          </Carousel>
        ) : (
          <Empty description="No cards to show" />
        )}
      </div>
    </>
  );
}
